import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/supabase-client'
import { toast } from 'sonner'

/**
 * CRUD for market_event_costs — Odin-owned cost data per market event.
 */
export function useMarketEventCosts(marketEventId) {
  const [costs, setCosts] = useState(null)
  const [loading, setLoading] = useState(true)

  const fetchCosts = useCallback(async () => {
    if (!marketEventId) return
    setLoading(true)
    const { data, error } = await supabase
      .from('market_event_costs')
      .select('*')
      .eq('market_event_id', marketEventId)
      .maybeSingle()

    if (error) {
      console.error('Failed to fetch market event costs:', error)
    } else {
      setCosts(data)
    }
    setLoading(false)
  }, [marketEventId])

  useEffect(() => {
    fetchCosts()
  }, [fetchCosts])

  const saveCosts = useCallback(async (costData) => {
    const payload = {
      market_event_id: marketEventId,
      stall_fee: costData.stall_fee || 0,
      staff_cost: costData.staff_cost || 0,
      travel_cost: costData.travel_cost || 0,
      other_cost: costData.other_cost || 0,
      notes: costData.notes || null,
      updated_at: new Date().toISOString()
    }

    let result
    if (costs?.id) {
      // Update existing
      result = await supabase
        .from('market_event_costs')
        .update(payload)
        .eq('id', costs.id)
        .select()
        .single()
    } else {
      // Insert new
      result = await supabase
        .from('market_event_costs')
        .insert(payload)
        .select()
        .single()
    }

    if (result.error) {
      toast.error('Failed to save costs', { description: result.error.message })
      throw result.error
    }

    setCosts(result.data)
    toast.success('Costs saved')
    return result.data
  }, [marketEventId, costs?.id])

  return { costs, loading, saveCosts, refresh: fetchCosts }
}
